import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { Modal } from 'antd';
import { buildLocaleOptions, getCountryFlag, getLanguageLabel } from 'utils/surveyLocaleUtils';

interface LanguageSelectorProps {
  currentLocale: string | null;
  defaultLocale: string | null;
  supportedLocales: string[];
  onChange: (locale: string | null) => void;
  fontFamily?: string;
  brandColor?: string;
}

const DEFAULT_BRAND_COLOR = '#d73e33';
const CANONICAL_VALUE = 'canonical';

export default function LanguageSelector({
  currentLocale,
  defaultLocale,
  supportedLocales,
  onChange,
  fontFamily,
  brandColor = DEFAULT_BRAND_COLOR,
}: LanguageSelectorProps) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(currentLocale ?? CANONICAL_VALUE);

  const options = useMemo(() => {
    if (!supportedLocales?.length) return [];
    const nonDefaultLocales = defaultLocale ? supportedLocales.filter((l) => l !== defaultLocale) : supportedLocales;
    const canonicalLabel = defaultLocale ? getLanguageLabel(defaultLocale) : t('survey.language.original');
    return buildLocaleOptions(nonDefaultLocales, canonicalLabel);
  }, [supportedLocales, defaultLocale, t]);

  const currentFlag = useMemo(() => {
    const locale = currentLocale ?? defaultLocale;
    return locale ? getCountryFlag(locale) : null;
  }, [currentLocale, defaultLocale]);

  const currentLabel = useMemo(() => {
    if (currentLocale) return getLanguageLabel(currentLocale);
    if (defaultLocale) return getLanguageLabel(defaultLocale);
    return t('survey.language.original');
  }, [currentLocale, defaultLocale, t]);

  const handleOpen = useCallback(() => {
    setSelectedValue(currentLocale ?? CANONICAL_VALUE);
    setIsOpen(true);
  }, [currentLocale]);

  const handleConfirm = useCallback(() => {
    const newLocale = selectedValue === CANONICAL_VALUE ? null : selectedValue;
    setIsOpen(false);
    onChange(newLocale);
  }, [selectedValue, onChange]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Nothing to switch to when the form/survey only lists its default locale.
  if (!options.length) {
    return null;
  }

  return (
    <Wrapper>
      <TriggerButton
        onClick={handleOpen}
        aria-label={t('survey.language.selectorLabel')}
        data-testid="language-selector"
        data-locale={currentLocale ?? ''}
      >
        {currentFlag ? <Flag>{currentFlag}</Flag> : <GlobeIcon />}
        <span>{currentLabel}</span>
        <ChevronIcon />
      </TriggerButton>

      <StyledModal
        $fontFamily={fontFamily}
        title={t('survey.language.modal.title')}
        visible={isOpen}
        onCancel={handleCancel}
        centered
        destroyOnClose
        closable
        footer={null}
        width={420}
      >
        <OptionsList data-testid="language-options-list">
          {options.map((opt) => {
            const value = opt.locale ?? CANONICAL_VALUE;
            const flagLocale = opt.locale ?? defaultLocale;
            const flag = flagLocale ? getCountryFlag(flagLocale) : null;
            const isSelected = selectedValue === value;

            return (
              <OptionItem
                key={value}
                $isSelected={isSelected}
                onClick={() => setSelectedValue(value)}
                role="radio"
                aria-checked={isSelected}
                data-testid={`language-option-${value}`}
              >
                <RadioCircle $isSelected={isSelected} $brandColor={brandColor}>
                  {isSelected && <RadioDot $brandColor={brandColor} />}
                </RadioCircle>
                {flag && <Flag>{flag}</Flag>}
                <OptionLabel $isSelected={isSelected} data-testid="language-option-label">
                  {opt.label}
                </OptionLabel>
              </OptionItem>
            );
          })}
        </OptionsList>

        <ModalFooter>
          <CancelButton onClick={handleCancel} data-testid="language-modal-cancel">
            {t('survey.language.modal.cancel')}
          </CancelButton>
          <ConfirmButton $brandColor={brandColor} onClick={handleConfirm} data-testid="language-modal-select">
            {t('survey.language.modal.select')}
          </ConfirmButton>
        </ModalFooter>
      </StyledModal>
    </Wrapper>
  );
}

function GlobeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 1C4.134 1 1 4.134 1 8s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm5.893 6.5h-2.91c-.093-1.74-.548-3.33-1.27-4.53A5.91 5.91 0 0 1 13.893 7.5zM8 13.9c-.87-1.03-1.52-2.58-1.65-4.4h3.3c-.13 1.82-.78 3.37-1.65 4.4zM6.35 8.5c.13-1.82.78-3.37 1.65-4.4.87 1.03 1.52 2.58 1.65 4.4h-3.3zm-.163-4.53C5.465 5.17 5.01 6.76 4.917 8.5h-2.81A5.91 5.91 0 0 1 6.187 3.97zM2.107 9.5h2.81c.093 1.74.548 3.33 1.27 4.53A5.91 5.91 0 0 1 2.107 9.5zm7.706 4.53c.722-1.2 1.177-2.79 1.27-4.53h2.81a5.91 5.91 0 0 1-4.08 4.53z"
        fill="#595959"
      />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 4.5L6 7.5L9 4.5" stroke="#595959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-bottom: 24px;
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
  color: #282e3e;
  font-family: inherit;

  &:hover {
    border-color: #b3b3b3;
  }
`;

const StyledModal = styled(Modal)<{ $fontFamily?: string }>`
  .ant-modal-content {
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-content,
  .ant-modal-content span,
  .ant-modal-content button {
    font-family: ${(props) => props.$fontFamily || 'inherit'} !important;
  }

  .ant-modal-header {
    padding: 20px 32px 8px;
    border-bottom: none;
    border-radius: 16px 16px 0 0;
  }

  .ant-modal-title {
    font-size: 24px !important;
    font-weight: 700 !important;
    line-height: 1.4 !important;
    color: #282e3e !important;
  }

  .ant-modal-body {
    padding: 12px 32px 0;
    display: flex;
    flex-direction: column;
    max-height: 60vh;
  }

  .ant-modal-close {
    top: 12px;
    inset-inline-end: -48px;
    width: 40px;
    height: 40px;
    background: #fff;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.16);

    .ant-modal-close-x {
      width: 40px;
      height: 40px;
      line-height: 40px;
      font-size: 16px;
    }

    @media (max-width: 520px) {
      inset-inline-end: 8px;
      top: 8px;
      background: transparent;
      box-shadow: none;
    }
  }

  .ant-modal-footer {
    display: none;
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  margin-top: 8px;
  padding-bottom: 80px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #eaeaea;
    border-radius: 8px;
  }
`;

const OptionItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #f9f9f9;
  }
`;

const RadioCircle = styled.div<{ $isSelected: boolean; $brandColor: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.$isSelected ? props.$brandColor : '#d9d9d9')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RadioDot = styled.div<{ $brandColor: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => props.$brandColor};
`;

const Flag = styled.span`
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
`;

const OptionLabel = styled.span<{ $isSelected: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: #282e3e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ModalFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 20px 32px;
  background: #fff;
  border-radius: 0 0 16px 16px;
`;

const CancelButton = styled.button`
  height: 40px;
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: #282e3e;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: #b3b3b3;
  }
`;

const ConfirmButton = styled.button<{ $brandColor: string }>`
  height: 40px;
  padding: 4px 12px;
  border-radius: 8px;
  border: none;
  background: ${(props) => props.$brandColor};
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    opacity: 0.9;
  }
`;
